import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  signal, AfterViewInit, computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { AccountFile, AccountFilesListResponse } from '@sbd-doug-frontend/shared/lib/types/file.types';
import { AccountFilesService } from '@sbd-doug-frontend/shared/lib/shared-services/account-files.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LongListModule } from '@sbd-doug-frontend/shared/lib/ui/longlist/longlist.module';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { FilterModule } from '@sbd-doug-frontend/shared/lib/ui/filter/filter.module';
import { AppFilters } from '@sbd-doug-frontend/shared/lib/ui/filter/AppFilters';
import { FilterCollection } from '@sbd-doug-frontend/shared/lib/ui/filter/FilterCollection';
import { QuickSearchData } from '@sbd-doug-frontend/shared/lib/ui/filter/filter.types';
import { CIPXActionBarComponent } from '@sbd-doug-frontend/shared/lib/ui/action-bar/action-bar.component';
import { CIPXPrimaryButtonComponent } from '@sbd-doug-frontend/shared/lib/ui/buttons/primary-button/primary-button.component';
import { CIPXCancelButtonComponent } from '@sbd-doug-frontend/shared/lib/ui/buttons/cancel-button/cancel-button.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FileNameFilter, FilePurposeFilter } from '@sbd-doug-frontend/shared/lib/ui/filter/account-file-filters';
import { finalize } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, Subject, takeUntil } from 'rxjs';
import {
  BondFilter,
  CompanyFilter, EmailFilter, FirstNameFilter, LastNameFilter,
  OrderNumberFilter, PhoneFilter,
  StateFilter,
} from '@sbd-doug-frontend/shared/lib/ui/filter/common-filters';
import { CIPXScrollableContentComponent } from '@sbd-doug-frontend/shared/lib/ui';

@Component({
  selector: 'account-files-list-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    LongListModule,
    MatCheckboxModule,
    MatButtonModule,
    FormsModule,
    MatIconModule,
    FilterModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    CIPXActionBarComponent,
    CIPXCancelButtonComponent,
    CIPXPrimaryButtonComponent,
    ReactiveFormsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CIPXScrollableContentComponent,
  ],
  templateUrl: './account-files-list-dialog.component.html',
  styleUrls: ['./account-files-list-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountFilesListDialogComponent implements OnInit, OnDestroy {
  private _cdr = inject(ChangeDetectorRef);
  private _accountFilesService = inject(AccountFilesService);
  private _sanitizer = inject(DomSanitizer);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  accountId: number;
  dataSource = new MatTableDataSource<AccountFile>([]);
  selection = new SelectionModel<AccountFile>(true, []);
  displayedColumns: string[] = ['select', 'file_name', 'surety_bond_label','is_shared_with_account', 'shared_status_label_with_account'];

  // selectedFile: AccountFile | null = null;
  selectedFilesList: AccountFile[] = [];
  // previewUrl: SafeResourceUrl | null = null;
  isLoading = computed(() => this._accountFilesService.accountFilesLoading());
  // isPreviewLoading = false;

  public appFilters: AppFilters = new AppFilters({
    appName: 'account_files_dialog',
    collections: [
      new FilterCollection({
        collectionLabel: 'File',
        collection: [
          new FileNameFilter(),
          new FilePurposeFilter(),
        ],
      }),
      new FilterCollection({
        collectionLabel: 'Quote',
        collection: [
          new OrderNumberFilter(),
          new CompanyFilter(),
          new BondFilter(),
          new StateFilter(),
        ],
      }),
      new FilterCollection({
        collectionLabel: 'Customer',
        collection: [
          new FirstNameFilter(),
          new LastNameFilter(),
        ],
      }),
    ],
  });
  public toggleFilter = false;
  public pageSize = 50;
  public pageIndex = 0;
  public total = 0;

  private _unsubscribeAll = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { accountId: number; selectedFiles?: AccountFile[] },
    private _dialogRef: MatDialogRef<AccountFilesListDialogComponent>,
  ) {
    this.accountId = data.accountId;
    if (data.selectedFiles && Array.isArray(data.selectedFiles)) {
      this.selectedFilesList = [...data.selectedFiles];
      data.selectedFiles.forEach(file => this.selection.select(file));
    }
  }

  ngOnInit(): void {
    this._dialogRef.keydownEvents().subscribe((event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.key === 'Escape' || keyboardEvent.key === 'Esc') {
        keyboardEvent.stopPropagation();
        keyboardEvent.preventDefault();
        this.onCancel();
      }
    });

    this.loadFiles();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loadFiles(resetToFirstPage = true, toggleFilterPanel = false): void {
    if (this.isLoading() === true) return;
    this.appFilters.interruptApplyFilter();

    if (resetToFirstPage && this.paginator) {
      this.paginator.pageIndex = 0;
    }


    const filters = this.appFilters.getAppliedFiltersURLParameters() || {};
    const params: Record<string, any> = { ...filters };
    params['limit'] = this.pageSize;
    params['offset'] = this.pageSize * this.pageIndex;


    this._cdr.markForCheck();

    this._accountFilesService.getAccountFiles(this.accountId, params).subscribe({
      next: (response: AccountFilesListResponse) => {
        this.total = response?.data?.files?.total_files
        const files = response?.data?.files?.files || [];
        this.dataSource.data = files;

        // Sync selection with new data
        if (this.selectedFilesList.length > 0) {
          files.forEach(file => {
            const isSelected = this.selectedFilesList.some(f => f.file_id === file.file_id);
            if (isSelected) {
              this.selection.select(file);
            }
          });
        }

        if (toggleFilterPanel) {
          this.toggleFilterPanel();
        }
        this._cdr.detectChanges();
      },
      error: () => {
        this._cdr.detectChanges();
      }
    });
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadFiles();
  }

  onSortChange(event: Sort) {
    this.pageIndex = 0;
    this.loadFiles();
  }

  selectFile(file: AccountFile): void {
    this.toggle(file, !this.selection.isSelected(file));
  }

  toggle(file: AccountFile, checked: boolean): void {
    if (checked) {
      this.selection.select(file);
      if (!this.selectedFilesList.find(f => f.file_id === file.file_id)) {
        this.selectedFilesList = [...this.selectedFilesList, file];
      }
    } else {
      this.selection.deselect(file);
      this.selectedFilesList = this.selectedFilesList.filter(f => f.file_id !== file.file_id);
    }
    this._cdr.markForCheck();
  }

  removeSelected(fileId: number): void {
    const file = this.selectedFilesList.find(f => f.file_id === fileId);
    if (file) {
      this.toggle(file, false);
    }
  }

  /** Whether the number of selected elements matches the total number of rows on the current page. */
  isAllSelected(): boolean {
    const numRows = this.dataSource.data.length;
    if (numRows === 0) return false;

    return this.dataSource.data.every(row =>
      this.selectedFilesList.some(f => f.file_id === row.file_id)
    );
  }

  /** Selects all rows on the current page if they are not all selected; otherwise clear them from selection. */
  masterToggle(): void {
    if (this.isAllSelected()) {
      // Deselect all rows on the current page
      this.dataSource.data.forEach(row => {
        this.selection.deselect(row);
        this.selectedFilesList = this.selectedFilesList.filter(f => f.file_id !== row.file_id);
      });
    } else {
      // Select all rows on the current page
      this.dataSource.data.forEach(row => {
        this.selection.select(row);
        if (!this.selectedFilesList.find(f => f.file_id === row.file_id)) {
          this.selectedFilesList.push(row);
        }
      });
      this.selectedFilesList = [...this.selectedFilesList];
    }
    this._cdr.markForCheck();
  }

  /*
  previewFile(file: AccountFile): void {
    this.selectedFile = file;
    this.isPreviewLoading = true;
    this.previewUrl = null;
    this._cdr.markForCheck();

    this._accountFilesService.readFile(file.quote_request_id, file.file_id).subscribe({
      next: (blob: Blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          this.previewUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
        }
        this.isPreviewLoading = false;
        this._cdr.markForCheck();
      },
      error: () => {
        this.isPreviewLoading = false;
        this._cdr.markForCheck();
      }
    });
  }
  */

  close(): void {
    this._dialogRef.close();
  }

  onCancel(): void {
    this.close();
  }

  confirmSelection(file?: AccountFile): void {
    if (file) {
      this._dialogRef.close([file]);
      return;
    }

    if (this.selectedFilesList.length > 0) {
      this._dialogRef.close(this.selectedFilesList);
    }
  }

  onAddSelected(): void {
    this.confirmSelection();
  }

  onQuickSearchSelected(event: QuickSearchData) {
    this.appFilters.onQuickSearchSelected(event);
    this.loadFiles();
  }

  onQuickSearchFocused(_focused: boolean) {
    // no-op
  }

  toggleFilterPanel() {
    this.toggleFilter = !this.toggleFilter;
    this._cdr.markForCheck();
  }

  onFilterChange(_fg: unknown) {
    this.loadFiles();
  }

  onRemoveFilter(_fg: unknown) {
    this.loadFiles();
  }

  onApplyFilterButtonPressed(_toggle: boolean) {
    this.loadFiles(true, true);
  }

  getFileIcon(extension: string): string {
    switch (extension?.toLowerCase()) {
      case 'pdf': return 'heroicons_outline:document-text';
      case 'png':
      case 'jpg':
      case 'jpeg': return 'heroicons_outline:photograph';
      case 'doc':
      case 'docx': return 'heroicons_outline:document';
      default: return 'heroicons_outline:document';
    }
  }
}
